import {
    ColDef,
    IAfterGuiAttachedParams,
    IDoesFilterPassParams,
    IFilterComp,
    IFilterParams,
    IFloatingFilterComp,
    IFloatingFilterParams,
} from '@ag-grid-community/core';
import { Box, Flex } from '@chakra-ui/react';
import { createRoot, Root } from 'react-dom/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Provider } from '@/components/ui/provider';
import { FilterOption } from '@/components/ui/responsive-grid/types';

export interface BranchOption {
    id: string;
    name: string;
}

export interface BranchFilterParams extends IFilterParams {
    branches: BranchOption[];
}

export interface BranchFilterModel {
    values: string[];
}

/** The single-select filter's persisted model shape, pre-multi-select. */
interface LegacyBranchFilterModel {
    value: string;
}

function isLegacyBranchFilterModel(model: object): model is LegacyBranchFilterModel {
    return 'value' in model;
}

/** How long to wait after the last checkbox click before committing the selection and triggering a grid refetch. */
export const COMMIT_DEBOUNCE_MS = 300;

export function branchesFromSession(session: { scope?: { branches?: BranchOption[] } } | null | undefined): BranchOption[] {
    return session?.scope?.branches?.map((branch) => ({ id: branch.id, name: branch.name })) ?? [];
}

export function branchFilterValueMaps(branches: BranchOption[]): Record<string, Record<string, string>> {
    const map = Object.fromEntries(branches.map((branch) => [branch.id, branch.name]));
    return { branch: map, branchName: map };
}

/** Shared "1 selected shows its name, 2+ shows a count" rule used by both branch-selection UIs. */
export function formatBranchSelectionLabel(selectedIds: string[], branches: BranchOption[]): string {
    if (selectedIds.length === 1) {
        return branches.find((branch) => branch.id === selectedIds[0])?.name ?? selectedIds[0];
    }
    return `${selectedIds.length} Branches`;
}

export function branchColumnFilter(branches: BranchOption[]) {
    return {
        filter: BranchFilter,
        filterParams: { branches },
        floatingFilterComponent: BranchFloatingFilter,
    };
}

function isBranchColumn<T>(col: ColDef<T>): boolean {
    const field = String(col.field ?? '');
    return col.headerName === 'Branch' || field === 'branch' || field === 'branchName';
}

export function applyBranchColumnFilter<T>(colDefs: ColDef<T>[] | undefined, branches: BranchOption[]): ColDef<T>[] {
    return (colDefs ?? []).map((col) => (isBranchColumn(col) ? { ...col, ...branchColumnFilter(branches) } : col));
}

interface BranchFilterChecklistProps {
    branches: BranchOption[];
    selectedIds: string[];
    onToggleAll: () => void;
    onToggleBranch: (branchId: string) => void;
}

function BranchFilterChecklist({ branches, selectedIds, onToggleAll, onToggleBranch }: BranchFilterChecklistProps) {
    return (
        <Provider>
            <Box p="8px" minW="200px">
                <Checkbox size="sm" mb="6px" checked={selectedIds.length === 0} onCheckedChange={onToggleAll}>
                    All Branches
                </Checkbox>
                <Flex direction="column" gap="6px" maxH="220px" overflowY="auto">
                    {branches.map((branch) => (
                        <Checkbox
                            key={branch.id}
                            size="sm"
                            checked={selectedIds.includes(branch.id)}
                            onCheckedChange={() => onToggleBranch(branch.id)}>
                            {branch.name}
                        </Checkbox>
                    ))}
                </Flex>
            </Box>
        </Provider>
    );
}

export class BranchFilter implements IFilterComp {
    filterParams!: BranchFilterParams;
    filterValues!: string[];
    gui!: HTMLDivElement;
    private root!: Root;
    private commitTimer?: ReturnType<typeof setTimeout>;
    // Precomputed once per selection change (not per row) — doesFilterPass runs once per grid row.
    private selectedIdSet: Set<string> = new Set();
    private selectedNameSet: Set<string> = new Set();

    /** Multi-select custom filter — consumed by CardFilterBar's mobile/card filter host. */
    static multiple = true;

    static getFilterOptions(params?: { branches?: BranchOption[] }): FilterOption[] {
        const branches = params?.branches ?? [];
        return [{ value: '', label: 'All Branches' }, ...branches.map((b) => ({ value: b.id, label: b.name }))];
    }

    init(params: BranchFilterParams) {
        this.filterParams = params;
        this.filterValues = [];
        this.gui = document.createElement('div');
        this.gui.className = 'ag-filter-body-wrapper';
        this.root = createRoot(this.gui);
        this.renderChecklist();
    }

    private syncSelectedSets() {
        this.selectedIdSet = new Set(this.filterValues);
        this.selectedNameSet = new Set(
            this.filterParams.branches.filter((branch) => this.selectedIdSet.has(branch.id)).map((branch) => branch.name),
        );
    }

    // Debounced so checking several branches in a row commits — and refetches the grid — once,
    // instead of once per click.
    private notifyChange() {
        if (this.commitTimer) {
            clearTimeout(this.commitTimer);
        }
        this.commitTimer = setTimeout(() => {
            this.commitTimer = undefined;
            this.filterParams.filterChangedCallback();
        }, COMMIT_DEBOUNCE_MS);
    }

    private renderChecklist() {
        this.root.render(
            <BranchFilterChecklist
                branches={this.filterParams.branches}
                selectedIds={this.filterValues}
                onToggleAll={() => {
                    this.filterValues = [];
                    this.syncSelectedSets();
                    this.renderChecklist();
                    this.notifyChange();
                }}
                onToggleBranch={(branchId) => {
                    this.filterValues = this.filterValues.includes(branchId)
                        ? this.filterValues.filter((id) => id !== branchId)
                        : [...this.filterValues, branchId];
                    this.syncSelectedSets();
                    this.renderChecklist();
                    this.notifyChange();
                }}
            />,
        );
    }

    getGui() {
        return this.gui;
    }

    doesFilterPass(params: IDoesFilterPassParams) {
        const { node } = params;

        if (this.filterValues.length === 0) {
            return true;
        }

        // Prefer matching by id when the row carries one — some list DTOs only expose the branch
        // display name, so two branches sharing a name would otherwise be indistinguishable.
        const rowBranchId = (node.data as { branchId?: string } | undefined)?.branchId;
        if (rowBranchId) {
            return this.selectedIdSet.has(rowBranchId);
        }

        const branchName = this.filterParams.getValue(node)?.toString();
        return branchName != null && this.selectedNameSet.has(branchName);
    }

    isFilterActive() {
        return this.filterValues.length > 0;
    }

    getModel(): BranchFilterModel | null {
        if (!this.isFilterActive()) {
            return null;
        }

        return { values: this.filterValues };
    }

    setModel(model: BranchFilterModel | LegacyBranchFilterModel | null) {
        if (model && isLegacyBranchFilterModel(model)) {
            this.filterValues = model.value ? [model.value] : [];
        } else {
            this.filterValues = model?.values ?? [];
        }
        this.syncSelectedSets();
        this.renderChecklist();
    }

    afterGuiAttached(params?: IAfterGuiAttachedParams): void {
        if (!params?.suppressFocus) {
            this.gui.querySelector<HTMLInputElement>('input[type="checkbox"]')?.focus();
        }
    }

    destroy() {
        // Flush rather than drop a pending debounced commit — otherwise a checkbox click made
        // just before the filter is torn down (column removed, grid unmounted) is silently lost.
        if (this.commitTimer) {
            clearTimeout(this.commitTimer);
            this.filterParams.filterChangedCallback();
        }
        this.root.unmount();
    }
}

export class BranchFloatingFilter implements IFloatingFilterComp {
    private eGui!: HTMLDivElement;
    private eButton!: HTMLButtonElement;
    private params!: IFloatingFilterParams<BranchFilter>;
    private branches: BranchOption[] = [];

    init(params: IFloatingFilterParams<BranchFilter>) {
        this.params = params;
        this.eGui = document.createElement('div');
        this.eGui.style.width = '100%';
        this.eGui.style.height = '100%';
        this.eGui.style.display = 'flex';
        this.eGui.style.alignItems = 'center';

        this.eButton = document.createElement('button');
        this.eButton.type = 'button';
        this.eButton.className = 'ag-input-field-input ag-text-field-input';
        this.eButton.style.width = '100%';
        this.eButton.style.height = '24px';
        this.eButton.style.fontSize = '12px';
        this.eButton.style.textAlign = 'left';
        this.eButton.style.cursor = 'pointer';
        this.eButton.style.overflow = 'hidden';
        this.eButton.style.textOverflow = 'ellipsis';
        this.eButton.style.whiteSpace = 'nowrap';
        this.eButton.textContent = 'All Branches';

        this.params.parentFilterInstance((instance) => {
            this.branches = instance.filterParams.branches;
        });

        this.eButton.addEventListener('click', () => this.params.showParentFilter());

        this.eGui.appendChild(this.eButton);
    }

    getGui() {
        return this.eGui;
    }

    onParentModelChanged(parentModel: BranchFilterModel | null) {
        const values = parentModel?.values ?? [];

        if (values.length === 0) {
            this.eButton.textContent = 'All Branches';
            this.eButton.title = '';
            return;
        }

        const names = this.branches.filter((b) => values.includes(b.id)).map((b) => b.name);

        this.eButton.textContent = formatBranchSelectionLabel(values, this.branches);
        this.eButton.title = names.join(', ');
    }

    destroy() {
        // Cleanup if needed
    }
}
